import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { enqueueTask } from 'ember-concurrency';
import { scheduleOnce } from '@ember/runloop';
import { service } from '@ember/service';
import type Owner from '@ember/owner';
import { on } from '@ember/modifier';
import { t } from 'ember-intl';
import type IntlService from 'ember-intl/services/intl';
import type { TOC } from '@ember/component/template-only';
import {
  PhDotsThreeVertical,
  PhFile,
  PhFileCsv,
  PhFileDoc,
  PhFileImage,
  PhFileJpg,
  PhFilePdf,
  PhFilePng,
  PhFilePpt,
  PhFileText,
  PhFileTxt,
  PhFileXls,
  PhFileZip,
  PhInfo,
  PhTrash,
} from 'ember-phosphor-icons';
import Chip from 'tio-ui/components/chip';
import Popover from 'tio-ui/components/popover';
import Table from 'tio-ui/components/table';
import safeFormatDate from '../../../../helpers/safe-format-date.ts';
import { findRecord } from '@warp-drive/utilities/json-api';
import { reloadRecord, saveRecord, deleteExistingRecord } from 'tio-data/requests/save-record';
import { AiAnalysisWatcher } from '../../../../utils/tuition-assistance/ai-analysis-watcher.ts';
import { AI_READABLE_FILE_ACCEPT } from '../../../../utils/tuition-assistance/ai-validation.ts';
import type CableService from '../../../../services/cable.ts';
import type { DocumentType } from 'tio-data/types/tuition-assistance';
import AttachmentUploadSection, {
  type AttachmentClassificationOption,
} from '../../attachment-upload-section.gts';
import { DrawerHeader, DrawerBody } from 'tio-ui/components/drawer';
import type { TasApplicationExtension as TasApplicationModel } from 'tio-data/data/resources/tas-application.ext';
import type { TasAssetExtension as TasAssetModel } from 'tio-data/data/resources/tas-asset.ext';
import type { AssetSummaryExtension as AssetSummaryModel } from 'tio-data/data/resources/asset-summary.ext';
import type { TasProgramTemplateExtension as TasProgramTemplateModel } from 'tio-data/data/resources/tas-program-template.ext';
import type DigitalAssetsService from '../../../../services/digital-assets.ts';
import type { Store as StoreService } from '@warp-drive/core';

/**
 * Table View Component - Yields the table view of attachments
 */
interface TableViewSignature {
  Args: {
    sortedAttachments: TasAssetModel[];
    getFileIcon: (extension: string) => typeof PhFile;
    getClassificationLabel: (classification: string) => string;
    isDeleted: (asset: TasAssetModel) => boolean;
    deleteAttachment: { perform: (asset: TasAssetModel) => Promise<void>; isRunning: boolean };
    disabled?: boolean;
    showUploadSection: boolean;
  };
}

const TableView: TOC<TableViewSignature> = <template>
  {{#if @sortedAttachments.length}}
    <div class="overflow-x-auto">
      <Table data-test-attachments-table>
        <:thead as |options|>
          <options.tr>
            <options.th class="w-full max-w-xs">
              {{t "common.tas.approval.drawer.attachments.file_name" default="File Name"}}
            </options.th>
            <options.th>
              {{t "common.tas.approval.drawer.attachments.type" default="Type"}}
            </options.th>
            <options.th>
              {{t "common.tas.approval.drawer.attachments.date" default="Date"}}
            </options.th>
            <options.th />
          </options.tr>
        </:thead>
        <:tbody as |options|>
          {{#each @sortedAttachments as |asset|}}
            <options.tr data-test-attachment-row>
              {{! File Name Column }}
              <options.td class="w-full max-w-xs">
                <div
                  class="tio-row-tight overflow-hidden
                    {{if (@isDeleted asset) 'line-through tio-fg-muted' 'tio-fg-secondary'}}"
                >
                  {{#let (@getFileIcon asset.extension) as |IconComponent|}}
                    <IconComponent @size="20" class="shrink-0" />
                  {{/let}}
                  <a
                    href={{asset.signedUrl}}
                    target="_blank"
                    rel="noopener noreferrer"
                    class="tio-text-sm-semibold break-words min-w-0 hover:underline truncate"
                    data-test-attachment-filename
                  >
                    {{asset.filename}}
                  </a>
                </div>
              </options.td>

              {{! Type Badge Column }}
              <options.td>
                {{#let (@getClassificationLabel asset.classification) as |label|}}
                  {{#if label}}
                    <Chip
                      @size="sm"
                      @intent={{if
                        (eq asset.classification "CARD_USAGE_PROOF")
                        "warning"
                        "default"
                      }}
                      data-test-attachment-type-badge
                    >{{label}}</Chip>
                  {{/if}}
                {{/let}}
              </options.td>

              {{! Date Column }}
              <options.td>
                <span class="text-sm tio-fg-secondary" data-test-attachment-date>
                  {{safeFormatDate asset.createdAt month="2-digit" day="2-digit" year="2-digit"}}
                </span>
              </options.td>

              {{! Actions Column }}
              <options.td>
                {{! Always show actions menu }}
                <Popover @placement="bottom-end">
                  <:trigger as |pop|>
                    <button
                      type="button"
                      class="p-1 tio-fg-secondary hover:tio-fg-primary"
                      {{pop.register}}
                      {{on "click" pop.toggle}}
                      data-test-attachment-actions-trigger
                    >
                      <PhDotsThreeVertical @size="24" />
                    </button>
                  </:trigger>
                  <:body>
                    <div class="py-1 min-w-[160px]">
                      {{! View Info Action }}
                      <Popover @placement="left-start">
                        <:trigger as |pop|>
                          <button
                            type="button"
                            class="tio-menu-action"
                            {{pop.register}}
                            {{on "click" pop.toggle}}
                            data-test-view-info-action
                          >
                            <PhInfo @size="16" class="shrink-0" />
                            {{t
                              "common.tas.approval.drawer.attachments.view_info"
                              default="View Info"
                            }}
                          </button>
                        </:trigger>
                        <:body>
                          <div class="p-4 text-sm max-w-xs">
                            <div class="tio-col-tight">
                              <div>
                                <div class="font-semibold tio-fg-primary">
                                  {{t
                                    "common.tas.approval.drawer.attachments.file_name"
                                    default="File Name"
                                  }}
                                </div>
                                <div class="tio-fg-secondary break-words">
                                  {{asset.filename}}
                                </div>
                              </div>
                              <div>
                                <div class="font-semibold tio-fg-primary">
                                  {{t
                                    "common.tas.approval.drawer.attachments.file_type"
                                    default="File Type"
                                  }}
                                </div>
                                <div class="tio-fg-secondary">
                                  {{@getClassificationLabel asset.classification}}
                                </div>
                              </div>
                              <div>
                                <div class="font-semibold tio-fg-primary">
                                  {{t
                                    "common.tas.approval.drawer.attachments.upload_date"
                                    default="Upload Date"
                                  }}
                                </div>
                                <div class="tio-fg-secondary">
                                  {{safeFormatDate
                                    asset.createdAt
                                    month="2-digit"
                                    day="2-digit"
                                    year="numeric"
                                  }}
                                </div>
                              </div>
                              {{#if (@isDeleted asset)}}
                                <div>
                                  <div class="font-semibold tio-fg-primary">
                                    {{t
                                      "common.tas.approval.drawer.attachments.date_deleted"
                                      default="Date Deleted"
                                    }}
                                  </div>
                                  <div class="tio-fg-secondary">
                                    {{#if asset.attachmentDeletedAt}}
                                      {{safeFormatDate
                                        asset.attachmentDeletedAt
                                        month="2-digit"
                                        day="2-digit"
                                        year="numeric"
                                      }}
                                    {{/if}}
                                  </div>
                                </div>
                              {{/if}}
                              {{#if asset.size}}
                                <div>
                                  <div class="font-semibold tio-fg-primary">
                                    {{t "common.size" default="Size"}}
                                  </div>
                                  <div class="tio-fg-secondary">
                                    {{asset.size}}
                                    bytes
                                  </div>
                                </div>
                              {{/if}}
                            </div>
                          </div>
                        </:body>
                      </Popover>

                      {{! Delete Action - Only show if not deleted }}
                      {{#unless (@isDeleted asset)}}
                        {{#unless @disabled}}
                          <button
                            type="button"
                            class="tio-row-tight w-full px-4 py-2 text-sm tio-fg-danger hover:tio-bg-danger-soft"
                            {{on "click" (fn @deleteAttachment.perform asset)}}
                            disabled={{@deleteAttachment.isRunning}}
                            data-test-delete-action
                          >
                            <PhTrash @size="16" />
                            {{#if @deleteAttachment.isRunning}}
                              {{t "common.deleting" default="Deleting..."}}
                            {{else}}
                              {{t "common.delete" default="Delete"}}
                            {{/if}}
                          </button>
                        {{/unless}}
                      {{/unless}}
                    </div>
                  </:body>
                </Popover>
              </options.td>
            </options.tr>
          {{/each}}
        </:tbody>
      </Table>
    </div>
  {{else}}
    {{! Empty State }}
    {{#unless @showUploadSection}}
      <div class="tio-place-center py-12">
        <p class="text-sm tio-fg-muted text-center">
          {{t
            "tuition_assistance.program_details.no_attachments"
            default="No attachments yet. Click 'Upload Attachment' to add files."
          }}
        </p>
      </div>
    {{/unless}}
  {{/if}}
</template>;

/**
 * Redesigned Attachments drawer component
 * Simplified UI with upload toggle and actions menu
 * Now yields sections to allow app-specific rendering
 */
export interface AttachmentsDrawerRedesignSignature {
  Args: {
    model: TasApplicationModel;
    allAttachments: TasAssetModel[];
    classificationOptions?: AttachmentClassificationOption[];
    defaultClassification?: string;
    programTemplate?: TasProgramTemplateModel;
    onUploadComplete?: () => void;
    onAttachmentDelete?: (asset: TasAssetModel) => void;
    disabled?: boolean;
    hideDiscarded?: boolean;
  };
  Blocks: {
    default: [
      {
        UploadSection: typeof AttachmentUploadSection;
        uploadProps: {
          canUpload: boolean;
          showUploadSection: boolean;
          toggleUploadSection: () => void;
          selectedClassification: string;
          classificationDidChange: (key: string) => void;
          uploadFile: { perform: (file: unknown) => Promise<void>; isRunning: boolean };
          classificationOptions?: AttachmentClassificationOption[];
          categoryOptions: AttachmentClassificationOption[];
          selectedCategory: string | null;
          categoryDidChange: (key: string) => void;
          uploadDisabled: boolean;
          accept: string | undefined;
        };
        TableView: typeof TableView;
        tableProps: {
          sortedAttachments: TasAssetModel[];
          getFileIcon: (extension: string) => typeof PhFile;
          getClassificationLabel: (key: string) => string;
          isDeleted: (asset: TasAssetModel) => boolean;
          deleteAttachment: {
            perform: (asset: TasAssetModel) => Promise<void>;
            isRunning: boolean;
          };
          disabled?: boolean;
          showUploadSection: boolean;
        };
        attachments: TasAssetModel[];
        assetSummaries: AssetSummaryModel[];
        deleteAttachment: {
          perform: (asset: TasAssetModel) => Promise<void>;
          isRunning: boolean;
        };
        isDeleted: (asset: TasAssetModel) => boolean;
      },
    ];
  };
}

export default class AttachmentsDrawerRedesign extends Component<AttachmentsDrawerRedesignSignature> {
  @service declare store: StoreService;
  @service declare digitalAssets: DigitalAssetsService;
  @service declare intl: IntlService;
  @service declare cable: CableService;

  @tracked showUploadSection = false;
  @tracked selectedClassification =
    this.args.defaultClassification || 'APPLICATION_LIFECYCLE_DOCUMENTS';
  @tracked selectedCategory: string | null = null;

  /** Resolves AI verdicts for drawer uploads (subscribe → evaluate → persist). */
  #aiWatcher: AiAnalysisWatcher;

  constructor(owner: Owner, args: AttachmentsDrawerRedesignSignature['Args']) {
    super(owner, args);
    // Built here rather than as a field initializer — the injected services
    // are not readable until construction.
    this.#aiWatcher = new AiAnalysisWatcher({
      cable: this.cable,
      contextFor: (declaredType, uploadRowKey, asset) =>
        this.#aiContextFor(declaredType, uploadRowKey, asset),
      persist: (asset) => saveRecord(this.store, asset),
      reload: (asset) => {
        if (!asset.id) return Promise.resolve(null);
        return this.store.request(
          findRecord<TasAssetModel>('tas-asset', asset.id, {
            include: 'asset-summary',
            reload: true,
          })
        );
      },
      onVerdict: () => this.args.onUploadComplete?.(),
    });
    // Heal documents whose verdict never landed (the uploading surface closed
    // before the AI broadcast): when the summary is already persisted,
    // evaluate it now. Deferred past render so tag mutation cannot invalidate
    // values consumed earlier in this same render pass.
    // eslint-disable-next-line ember/no-runloop
    scheduleOnce('afterRender', this, this.#recoverPendingVerdicts);
  }

  willDestroy() {
    super.willDestroy();
    this.#aiWatcher.teardown();
  }

  #aiContextFor(declaredType: DocumentType, uploadRowKey: string, asset?: TasAssetModel) {
    const instance = this.args.model.tasProgramInstance;
    // Prefer the asset's own scope over this application's aggregate, so a
    // doc can't pass (or falsely fail) by matching a different attachable:
    // course-attached → its courses; application-attached → that application's
    // courses; program-level (no course/application link) → no course
    // expectations at all rather than inheriting this application's.
    const assetCourseNames = (asset?.tasCourses ?? []).map((course) => course.courseName);
    const assetApplicationCourseNames = (asset?.tasApplications ?? []).flatMap((application) =>
      application.activeCourses.map((course) => course.courseName)
    );
    const isProgramLevelAsset =
      !!asset &&
      !assetCourseNames.length &&
      !assetApplicationCourseNames.length &&
      (asset.tasProgramInstances?.length ?? 0) > 0;
    const expectedCourseNames = (
      assetCourseNames.length
        ? assetCourseNames
        : assetApplicationCourseNames.length
          ? assetApplicationCourseNames
          : isProgramLevelAsset
            ? []
            : this.args.model.activeCourses.map((course) => course.courseName)
    ).filter(Boolean);
    return {
      declaredType,
      uploadRowKey,
      employeeName: instance?.employee?.person?.fullName ?? '',
      expectedInstitutionName: instance?.institutionName ?? '',
      expectedCourseNames,
      translate: (key: string, options?: Record<string, string>) => this.intl.t(key, options),
    };
  }

  #recoverPendingVerdicts = () => {
    if (this.isDestroying || this.isDestroyed) return;
    void this.#aiWatcher.recoverPending(this.args.allAttachments ?? []);
  };

  // File extension to icon mapping
  getFileIcon = (extension: string) => {
    const ext = extension.toLowerCase();
    switch (ext) {
      case 'pdf':
        return PhFilePdf;
      case 'doc':
      case 'docx':
        return PhFileDoc;
      case 'xls':
      case 'xlsx':
        return PhFileXls;
      case 'ppt':
      case 'pptx':
        return PhFilePpt;
      case 'txt':
        return PhFileTxt;
      case 'zip':
        return PhFileZip;
      case 'jpg':
      case 'jpeg':
        return PhFileJpg;
      case 'png':
        return PhFilePng;
      case 'img':
        return PhFileImage;
      case 'csv':
        return PhFileCsv;
      case 'text':
        return PhFileText;
      default:
        return PhFile;
    }
  };

  // Classification label mapping
  getClassificationLabel = (classification: string): string => {
    const prefix = 'common.tas.approval.drawer.attachments';
    const classificationMap: Record<string, string> = {
      COMPLETION_DOCUMENTS: this.intl.t(`${prefix}.final_approval_document`),
      COURSE_APPROVAL_DOCUMENTS: this.intl.t(`${prefix}.approval_document`),
      PROGRAM_APPROVAL_DOCUMENTS: this.intl.t(`${prefix}.program_approval_document`),
      APPLICATION_LIFECYCLE_DOCUMENTS: this.intl.t(`${prefix}.approval_document_admin`),
      PROGRAM_COMPLETION_DOCUMENTS: this.intl.t(`${prefix}.program_completion_document`),
      CARD_USAGE_PROOF: this.intl.t(`${prefix}.card_usage_receipt`),
    };

    return classificationMap[classification] || '';
  };

  // Sorted attachments (non-deleted first, newest first; then deleted)
  get sortedAttachments(): TasAssetModel[] {
    const assets = this.args.allAttachments ?? [];

    const notDeleted = assets
      .filter((a) => a.attachmentDeletedAt === null)
      .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());

    if (this.args.hideDiscarded) {
      return notDeleted;
    }

    const deleted = assets.filter((a) => a.attachmentDeletedAt !== null);

    return [...notDeleted, ...deleted];
  }

  // Asset summaries for AI analysis
  get assetSummaries() {
    const summaries = this.args.allAttachments
      .map((asset) => asset.assetSummary)
      .filter((summary) => summary !== null);
    return summaries;
  }

  get canUpload(): boolean {
    return !this.args.disabled && !!this.args.programTemplate;
  }

  /**
   * Admin-configured document entries (label + AI document type) for the
   * currently selected classification, from the template's
   * `*_ATTACHMENT_FIELD`. Empty for classifications with no mapping
   * (e.g. APPLICATION_LIFECYCLE_DOCUMENTS) and for legacy templates.
   */
  get documentEntries() {
    return this.args.programTemplate?.documentEntriesFor(this.selectedClassification) ?? [];
  }

  get categoryOptions() {
    // Legacy templates can have attachment-field labels with no document-type
    // map — documentEntriesFor() resolves those all to 'general'. Mirror the
    // employee flow's fallback signal: only offer (and require) categories
    // when the template declares at least one AI-classifiable type.
    const template = this.args.programTemplate;
    if (!template?.requiredDocumentTypesFor(this.selectedClassification).length) {
      return [];
    }
    return this.documentEntries.map((entry) => ({ label: entry.label, key: entry.label }));
  }

  // When the template defines categories for the classification, picking one is required.
  get uploadDisabled() {
    return this.categoryOptions.length > 0 && !this.selectedCategory;
  }

  // Steer AI-analyzed uploads to formats the AI can read; general/misc
  // uploads stay unrestricted.
  get uploadAccept() {
    const entry = this.documentEntries.find((e) => e.label === this.selectedCategory);
    if (!entry || entry.type === 'general') return undefined;
    return AI_READABLE_FILE_ACCEPT;
  }

  // Toggle upload section visibility
  toggleUploadSection = () => {
    this.showUploadSection = !this.showUploadSection;
  };

  // Handle classification change (categories are per-classification, so reset)
  classificationDidChange = (key: string) => {
    this.selectedClassification = key;
    this.selectedCategory = null;
  };

  categoryDidChange = (key: string) => {
    this.selectedCategory = key;
  };

  // Upload file handler
  uploadFile = enqueueTask(async (file) => {
    // createAssetFromS3Details is typed to the looser base DigitalAsset trait,
    // which carries no relationship keys; this record is a tas-asset.
    const asset = this.digitalAssets.createAssetFromS3Details('tasAsset', file) as TasAssetModel;

    asset.classification = this.selectedClassification;

    // Tag with the selected category the same way the employee categorized
    // upload flow does (tuition-assistance/forms/categorized-upload) so the
    // document counts toward the configured required-document rows and the AI
    // pipeline picks it up. Tags must be set before the first save — the API
    // decides whether to dispatch AI analysis when the asset is created. No
    // category (misc/lifecycle docs, legacy templates) → 'general': marked
    // valid immediately and AI analysis is skipped.
    const entry = this.documentEntries.find((e) => e.label === this.selectedCategory);
    const documentType = entry?.type ?? 'general';
    asset.tags = {
      ...(entry ? { uploadRowKey: entry.label } : {}),
      employeeSelectedType: documentType,
      aiValidationStatus: documentType === 'general' ? 'valid' : 'pending',
      aiValidationDisplayStatus: documentType === 'general' ? 'valid' : 'pending',
      skipAiAnalysis: String(documentType === 'general'),
    };

    const attachable = this.args.model;

    // Ensure parent records are saved
    if (attachable.isNew) {
      if (attachable.tasProgramInstance?.isNew) {
        await saveRecord(this.store, attachable.tasProgramInstance);
      }
      await saveRecord(this.store, attachable);
    }

    asset.tasApplications.push(attachable);
    attachable.tasAssets.push(asset);

    // Watch for the AI verdict BEFORE the create request dispatches analysis —
    // subscribing after the saves leaves a gap where a fast broadcast could be
    // missed, stranding the asset at `pending` until the timeout fallback.
    if (entry && documentType !== 'general') {
      this.#aiWatcher.watch(asset, entry.label, documentType);
    }
    try {
      await saveRecord(this.store, asset);
    } catch (e) {
      // The create never happened, so no analysis was dispatched — drop the
      // watch. A failing parent save below must NOT unwatch: the created
      // asset has already dispatched analysis and its verdict should land.
      this.#aiWatcher.unwatch(asset.uuid);
      throw e;
    }
    // The create settled — buffered verdicts may now persist safely.
    this.#aiWatcher.markCreated(asset.uuid);
    await saveRecord(this.store, attachable);

    // Close upload section and trigger callback
    this.showUploadSection = false;
    this.args.onUploadComplete?.();
  });

  // Delete attachment handler
  deleteAttachment = enqueueTask(async (asset: TasAssetModel) => {
    const attachment = asset.attachments[0];
    if (!attachment) {
      console.warn('No attachment found for asset', asset);
      return;
    }

    await deleteExistingRecord(this.store, attachment);
    await reloadRecord(this.store, asset);

    this.args.onAttachmentDelete?.(asset);
  });

  // Download attachment

  // Check if attachment is deleted
  isDeleted = (asset: TasAssetModel): boolean => {
    return asset.attachmentDeletedAt !== null;
  };

  <template>
    <DrawerHeader>
      <h2 class="tio-text-heading tio-fg-primary">
        {{t "common.tas.approval.drawer.attachments.title" default="Attachments"}}
      </h2>
    </DrawerHeader>

    <DrawerBody>
      <div class="tio-col-extra-loose p-6">
        {{! Yield to allow app-specific rendering }}
        {{yield
          (hash
            UploadSection=AttachmentUploadSection
            uploadProps=(hash
              canUpload=this.canUpload
              showUploadSection=this.showUploadSection
              toggleUploadSection=this.toggleUploadSection
              selectedClassification=this.selectedClassification
              classificationDidChange=this.classificationDidChange
              uploadFile=this.uploadFile
              classificationOptions=@classificationOptions
              categoryOptions=this.categoryOptions
              selectedCategory=this.selectedCategory
              categoryDidChange=this.categoryDidChange
              uploadDisabled=this.uploadDisabled
              accept=this.uploadAccept
            )
            TableView=TableView
            tableProps=(hash
              sortedAttachments=this.sortedAttachments
              getFileIcon=this.getFileIcon
              getClassificationLabel=this.getClassificationLabel
              isDeleted=this.isDeleted
              deleteAttachment=this.deleteAttachment
              disabled=@disabled
              showUploadSection=this.showUploadSection
            )
            attachments=this.sortedAttachments
            assetSummaries=this.assetSummaries
            deleteAttachment=this.deleteAttachment
            isDeleted=this.isDeleted
          )
        }}
      </div>
    </DrawerBody>
  </template>
}
